import { useEffect, useState, useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import axios from "../../plugins/axios";
import {
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Button,
    Box,
    Typography,
    IconButton,
    Grid,
    Switch,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";
import { useDropzone, type FileWithPath } from "react-dropzone";
import AccountAutoComplete, { type AccountOption } from "../AccountAutoComplete";

const useStyles = makeStyles()(() => ({
    label: {
        fontSize: "0.8em",
        fontWeight: "bold",
    },
    drop: {
        display: "flex",
        padding: "20px",
        background: "#eee",
        borderRadius: "5px",
        margin: "10px",
        flexDirection: "column" as const,
    },
    safetyToggle: {
        display: "flex",
        justifyContent: "space-between",
        background: "#f4eeff",
        borderRadius: "5px",
        alignItems: "center",
        paddingLeft: "12px",
    },
    adviceType: {
        border: "1px solid #444",
        padding: "0px 3px",
        background: "#ddd",
        borderRadius: "4px",
        marginRight: "5px",
    },
}));

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

interface Contract {
    key: string | number;
    value: string;
}

interface Contact {
    id: string | number;
    name: string;
    adviceTypes: string[];
}

export interface TimesheetEntry {
    id?: number | string;
    accountId?: number | string | null;
    accountName?: string;
    contractId?: number | string;
    contracts?: Contract[];
    contactId?: number | string;
    contactName?: string;
    description?: string;
    activityType?: string;
    advisoryType?: string;
    startTime?: Date | null;
    endTime?: Date | null;
    publishToSafetyNest?: boolean;
    files?: FileWithPath[];
}

type EntryState = TimesheetEntry | null | undefined;

interface Props {
    entry: TimesheetEntry;
    setEntry: Dispatch<SetStateAction<EntryState>>;
    date: Date;
    activityTypes: string[];
    advisoryTypes: string[];
    calcDuration: (start: Date | null | undefined, end: Date | null | undefined) => string;
    onSubmitted: () => void;
}

export default function TimesheetEntryDialog({
    entry,
    setEntry,
    date,
    activityTypes,
    calcDuration,
    advisoryTypes,
    onSubmitted,
}: Props) {
    const { classes } = useStyles();

    const [contacts, setContacts] = useState<Contact[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(date);

    useEffect(() => {
        if (!entry.accountId) return;

        const fetchContacts = async () => {
            const response = await axios.get<Contact[]>(`${apiUrl}/getContacts/${entry.accountId}`);
            setContacts(response.data);
        };

        fetchContacts();
    }, [entry.accountId]);

    const removeFile = (file: FileWithPath) => {
        setEntry({
            ...entry,
            files: (entry.files || []).filter((f) => f !== file),
        });
    };

    const handleAccountChange = (v: AccountOption | null) => {
        setEntry({
            ...entry,
            accountId: v ? v.value : null,
            accountName: v ? v.text : undefined,
        });
    };

    useEffect(() => {
        if (entry.accountId == null) {
            setEntry((prev) =>
                prev
                    ? {
                          ...prev,
                          contracts: [],
                          contractId: "",
                      }
                    : prev,
            );
        } else {
            (async () => {
                const result = await axios.get<Contract[]>(`${apiUrl}/accountcontracts/${entry.accountId}`);
                const contracts = result.data;
                setEntry((prev) =>
                    prev
                        ? {
                              ...prev,
                              contracts,
                              contractId:
                                  prev.contractId || (contracts.length === 1 ? contracts[0].key : ""),
                          }
                        : prev,
                );
            })();
        }
    }, [entry.accountId, setEntry]);

    const updateEntry = useCallback(
        <K extends keyof TimesheetEntry>(field: K, value: TimesheetEntry[K]) => {
            setEntry((prev) => (prev ? { ...prev, [field]: value } : prev));
        },
        [setEntry],
    );

    const contracts = entry.contracts || [];

    const updateTimeEntry = (field: "startTime" | "endTime", value: Date | null, baseDate?: Date) => {
        if (!value) {
            updateEntry(field, null);
            return;
        }
        const dt = new Date(baseDate || selectedDate);
        dt.setHours(value.getHours());
        dt.setMinutes(value.getMinutes());
        dt.setSeconds(0);
        updateEntry(field, dt);
    };

    const setDate = (newDate: Date | null) => {
        if (!newDate) return;
        setSelectedDate(newDate);

        if (entry.startTime) updateTimeEntry("startTime", entry.startTime, newDate);
        if (entry.endTime) updateTimeEntry("endTime", entry.endTime, newDate);
    };

    const onDrop = useCallback(
        (acceptedFiles: FileWithPath[]) => {
            const existing = entry.files || [];
            const newFiles = acceptedFiles.filter(
                (f) => existing.filter((e) => e.path === f.path).length === 0,
            );
            setEntry((prev) =>
                prev
                    ? {
                          ...prev,
                          files: [...existing, ...newFiles],
                      }
                    : prev,
            );
        },
        [entry.files, setEntry],
    );

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    const submit = async () => {
        setSubmitting(true);
        try {
            const formData = new FormData();

            if (entry.files) {
                entry.files.forEach((file) => {
                    formData.append("files", file);
                });
            }

            formData.append("entryJson", JSON.stringify(entry));

            await axios.post(`${apiUrl}/addtimesheetentry`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            onSubmitted();
        } finally {
            setSubmitting(false);
        }
    };

    const isValid = useMemo(() => {
        if (entry.accountId && !entry.contractId) return false;
        if (!entry.accountId && !entry.description) return false;
        if (!entry.activityType) return false;

        const duration = calcDuration(entry.startTime, entry.endTime);
        if (duration === "Invalid" || duration === "") return false;

        return true;
    }, [calcDuration, entry]);

    const handleClose = (_event: object, reason: string) => {
        if (reason === "backdropClick") return;
        setEntry(undefined);
    };

    return (
        <Dialog onClose={handleClose} open={!!entry}>
            <DialogContent>
                <Typography variant="h6">Add Entry</Typography>
                <Box display="flex" alignItems="center" gap="15px">
                    <Typography style={{ fontSize: "0.8em" }}>Submit an entry for</Typography>
                    <DatePicker
                        value={selectedDate}
                        onChange={setDate}
                        slotProps={{
                            textField: {
                                size: "small",
                                InputProps: {
                                    style: {
                                        width: "150px",
                                    },
                                },
                            },
                            field: {
                                readOnly: true,
                            },
                        }}
                    />
                </Box>
                <Box display="flex" gap="5px" flexDirection="column">
                    <Grid container spacing={2}>
                        <Grid size={7}>
                            <Typography className={classes.label}>Account</Typography>
                            {entry.id ? (
                                <TextField disabled value={entry.accountName} fullWidth size="small" />
                            ) : (
                                <AccountAutoComplete handleAccountChange={handleAccountChange} />
                            )}
                        </Grid>
                        <Grid size={5}>
                            <Typography className={classes.label}>Contract</Typography>
                            <FormControl style={{ width: "100%" }} disabled={!!entry.id}>
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={entry.contractId || ""}
                                    onChange={(e) => updateEntry("contractId", e.target.value)}
                                >
                                    {contracts.length === 0 && entry.accountId == null && (
                                        <MenuItem value="">
                                            <em>&lt;-- choose Account</em>
                                        </MenuItem>
                                    )}
                                    {contracts.length === 0 && entry.accountId && (
                                        <MenuItem value="">
                                            <em>none available</em>
                                        </MenuItem>
                                    )}
                                    {contracts.length > 1 && (
                                        <MenuItem value="">
                                            <em>select contract</em>
                                        </MenuItem>
                                    )}
                                    {contracts.map((c) => (
                                        <MenuItem key={c.key} value={c.key}>
                                            {c.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Box display="flex" flexDirection="column">
                        <Typography className={classes.label}>Description</Typography>
                        <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={4}
                            inputProps={{ maxLength: 8192 }}
                            value={entry.description || ""}
                            onChange={(e) => updateEntry("description", e.target.value)}
                        />
                    </Box>
                    <FormControl style={{ width: "100%" }} disabled={!!entry.id}>
                        <Typography className={classes.label}>Activity Type</Typography>
                        <Select
                            displayEmpty
                            size="small"
                            value={entry.activityType || ""}
                            onChange={(e) => updateEntry("activityType", e.target.value)}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {activityTypes.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(entry.activityType === "Advisory" || entry.activityType === "Remote Advisory") &&
                        entry.accountId && (
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Typography className={classes.label}>Advisory Type</Typography>
                                    <Select
                                        disabled={!!entry.id}
                                        fullWidth
                                        displayEmpty
                                        size="small"
                                        value={entry.advisoryType || ""}
                                        onChange={(e) => updateEntry("advisoryType", e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {advisoryTypes.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                                <Grid size={6}>
                                    <Typography className={classes.label}>Contact</Typography>
                                    {entry.id ? (
                                        <TextField
                                            disabled
                                            value={entry.contactName}
                                            fullWidth
                                            size="small"
                                        />
                                    ) : (
                                        <Select
                                            fullWidth
                                            displayEmpty
                                            size="small"
                                            value={entry.contactId || ""}
                                            onChange={(e) => updateEntry("contactId", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {contacts.map((c) => (
                                                <MenuItem key={c.id} value={c.id}>
                                                    <Box display="flex" flexDirection="column">
                                                        <Box>{c.name}</Box>
                                                        <Box>
                                                            {c.adviceTypes.map((at) => (
                                                                <span className={classes.adviceType} key={at}>
                                                                    {at}
                                                                </span>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                </Grid>
                                <Grid size={12}>
                                    <div className={classes.safetyToggle}>
                                        <Typography className={classes.label}>
                                            Would you like to add this advice to SafetyNest?
                                        </Typography>
                                        <Switch
                                            checked={entry.publishToSafetyNest || false}
                                            onChange={(e) =>
                                                updateEntry("publishToSafetyNest", e.target.checked)
                                            }
                                            disabled={!!entry.id}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        )}
                    <Grid container spacing={2}>
                        <Grid size={5}>
                            <Typography className={classes.label}>Start</Typography>
                            <TimePicker
                                value={entry.startTime || null}
                                onChange={(value) => updateTimeEntry("startTime", value)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                    },
                                    field: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={5}>
                            <Typography className={classes.label}>End</Typography>
                            <TimePicker
                                value={entry.endTime || null}
                                onChange={(value) => updateTimeEntry("endTime", value)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                    },
                                    field: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <Typography className={classes.label}>Duration</Typography>
                            <Typography pl={2} pt={2}>
                                {calcDuration(entry.startTime, entry.endTime)}
                            </Typography>
                        </Grid>
                    </Grid>
                    <section>
                        <div className={classes.drop} {...getRootProps()}>
                            <input {...getInputProps()} />
                            <Box display="flex" alignItems="center">
                                <CloudUploadIcon style={{ marginRight: "5px" }} />
                                <Typography style={{ cursor: "default" }}>
                                    Drag and drop your files here or <strong>click</strong> to browse
                                </Typography>
                            </Box>
                            {entry.files && (
                                <ul>
                                    {entry.files.map((file) => (
                                        <li key={file.path}>
                                            <Box alignItems="center" display="flex">
                                                <Box>{file.path} </Box>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeFile(file);
                                                    }}
                                                    size="large"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </section>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEntry(undefined)}>Cancel</Button>
                <Button onClick={submit} color="primary" disabled={submitting || !isValid}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
